/**
 * useSpendingReport Hook
 *
 * A React hook for fetching spending by category report data from the API.
 * Provides spending breakdown by expense account for pie/bar chart visualization.
 *
 * @see docs/plans/iterations/transaction-reporting/contracts/frontend-rails-api.json
 */

import { useState, useCallback } from 'react';
import type { SpendingReportResponse, Owner } from '@/types/accounting';

/**
 * Return type for the useSpendingReport hook
 */
export interface UseSpendingReportReturn {
  /** Current spending report data */
  data: SpendingReportResponse | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch spending report with current parameters */
  fetchReport: () => Promise<SpendingReportResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for fetching spending by category report
 *
 * @param startDate - Start date of the report period (ISO date string: "2025-01-01")
 * @param endDate - End date of the report period (ISO date string: "2025-01-31")
 * @param owner - Optional owner filter ("family" or "estate")
 * @returns Object containing data, loading state, error, and fetch function
 *
 * @example
 * ```typescript
 * const { data, loading, error, fetchReport } = useSpendingReport(
 *   '2025-01-01',
 *   '2025-01-31',
 *   'family'
 * );
 *
 * // Fetch report on mount
 * useEffect(() => {
 *   fetchReport();
 * }, [fetchReport]);
 *
 * // Display spending categories
 * if (data) {
 *   data.categories.forEach(cat => {
 *     console.log(`${cat.accountName}: ${cat.percentage}%`);
 *   });
 * }
 * ```
 */
export function useSpendingReport(
  startDate: string,
  endDate: string,
  owner?: Owner
): UseSpendingReportReturn {
  const [data, setData] = useState<SpendingReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch spending report from the API
   */
  const fetchReport = useCallback(async (): Promise<SpendingReportResponse> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      if (owner) {
        params.set('owner', owner);
      }

      const response = await fetch(`/api/accounting/reports/spending_by_category?${params}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const json = (await response.json()) as SpendingReportResponse;
      setData(json);
      return json;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load spending report';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, owner]);

  return { data, loading, error, fetchReport, clearError };
}
